import { ListObjectsCommand } from "@aws-sdk/client-s3";
import { applyPhotoOrder } from "./photoOrder";
import {
  S3_PHOTOS_BUCKET,
  albumPrefixes,
  photoUrl,
  thumbUrl,
} from "./media";

/**
 * One listing's photos, for the realtor panel.
 *
 * The public site has its own copy of this in `public/api/publicApi.js`, which
 * runs on anonymous credentials. The panel must not borrow those: it already
 * holds an authenticated S3 client, and pulling the public module in would spin
 * up a second Cognito identity inside the admin bundle for no reason.
 *
 * Cached per listing id, because the card grid asks for the same album every
 * time the view is toggled or the page re-renders, and each miss is a real S3
 * LIST request. `invalidateAlbum` exists so uploading or deleting a photo does
 * not leave a stale cover on the card.
 */

const cache = {};
const inflight = {};
const listeners = {};

/**
 * How many times this album has been invalidated.
 *
 * A listing is added to the list before its photos finish uploading, so the
 * card mounts and lists an album that is still empty. Clearing the cache alone
 * does not fix that: the empty LIST is already in flight, and it would write
 * its result into the cache after the invalidation, putting the stale answer
 * straight back. A reply is only allowed to write the cache if the album has
 * not been invalidated since its request went out.
 */
const generation = {};

/** Resolves to [{ key, name, url, thumbUrl }] — same shape the dialogs use. */
export function loadAlbum(s3Client, objectId, order) {
  if (!s3Client || !objectId) return Promise.resolve([]);
  if (cache[objectId]) return Promise.resolve(cache[objectId]);
  if (inflight[objectId]) return inflight[objectId];

  const prefix = albumPrefixes(objectId)[0];
  const asked = generation[objectId] || 0;

  inflight[objectId] = s3Client
    .send(new ListObjectsCommand({ Bucket: S3_PHOTOS_BUCKET, Prefix: prefix }))
    .then(function (data) {
      const photos = (data.Contents || [])
        .map(function (entry) {
          return entry.Key;
        })
        .filter(function (key) {
          return key && !/\/$/.test(key);
        })
        .sort()
        .map(function (key) {
          return {
            key: key,
            name: key.split("/").pop(),
            url: photoUrl(key),
            thumbUrl: thumbUrl(key),
          };
        });

      const sorted = applyPhotoOrder(photos, order);
      if ((generation[objectId] || 0) === asked) cache[objectId] = sorted;
      if (inflight[objectId]) delete inflight[objectId];
      return sorted;
    })
    .catch(function (err) {
      if (inflight[objectId]) delete inflight[objectId];
      // A missing cover is a cosmetic problem; never let it break the grid.
      console.warn("Не вдалося прочитати фото оголошення " + objectId, err);
      return [];
    });

  return inflight[objectId];
}

/**
 * Forget this album and tell every card showing it to read again.
 *
 * Called after an upload and after a photo is deleted. The notification is the
 * half that matters for a new listing: its card is already mounted by then,
 * and a mounted card re-runs its effect only when the listing id changes —
 * which, for the listing that was just added, it never does.
 */
export function invalidateAlbum(objectId) {
  if (!objectId) return;
  generation[objectId] = (generation[objectId] || 0) + 1;
  delete cache[objectId];
  delete inflight[objectId];
  const subscribers = listeners[objectId];
  if (!subscribers) return;
  // Copied, so a listener that unsubscribes while being called cannot make the
  // loop skip its neighbour.
  subscribers.slice().forEach(function (fn) {
    fn();
  });
}

/** Call `fn` whenever this album is invalidated. Returns an unsubscribe. */
export function subscribeAlbum(objectId, fn) {
  if (!objectId) return function () {};
  if (!listeners[objectId]) listeners[objectId] = [];
  listeners[objectId].push(fn);
  return function () {
    if (!listeners[objectId]) return;
    listeners[objectId] = listeners[objectId].filter(function (other) {
      return other !== fn;
    });
    if (listeners[objectId].length === 0) delete listeners[objectId];
  };
}
